import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BaseComponent, DataTableRequest, LanguageService, UserSelectListCheckBoxComponent, SearchData, ValidationHandlerService, VestraUserSearchData } from 'lib/Vestra.shared';
import { BsModalService } from 'ngx-bootstrap/modal';
import { VestraUserSelectType, VestraUserService } from 'src/app/services/api-services';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom } from 'rxjs';
import { ADTColumns, ADTSettings } from 'angular-datatables/src/models/settings';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

@Component({
    selector: 'app-vestra-user-select',
    templateUrl: './vestra-user-select.component.html',
    styleUrl: './vestra-user-select.component.scss',
})
export class VestraUserSelectComponent extends BaseComponent implements OnInit, AfterViewInit {
    @ViewChild('vestraUserSelectCheckboxRef') vestraUserSelectCheckboxRef: TemplateRef<UserSelectListCheckBoxComponent>;
    @Input() title: string;

    @Input() vestraUserSelectType: VestraUserSelectType;

    @Output()
    public selectedUserIds: EventEmitter<number[]> = new EventEmitter();
    public userIds: number[] = [];

    constructor(
        private router: Router,
        protected override validationHandlerService: ValidationHandlerService,
        protected override modalService: BsModalService,
        protected override spinnerService: NgxSpinnerService,
        protected translateService: TranslateService,
        protected override languageService: LanguageService,
        private vestraUserService: VestraUserService
    ) {
        super(spinnerService, modalService, validationHandlerService, translateService, languageService);
    }

    public ngOnInit(): void {
        this.dataTableOnInit();
    }

    public ngAfterViewInit() {
        this.dataTableAfterViewInit();
    }

    public ngOnDestroy() {
        this.dataTableOnDestroy();
    }

    public override getDtOptions() {
        let self = this;
        this.languageUrl = this.languageService.getTranslationUrl();
        const columns = this.getColumns();
        const res: ADTSettings = {
            serverSide: true,
            processing: false,
            pageLength: 20,
            lengthChange: false,
            responsive: {
                details: {
                    type: 'column',
                    target: -1,
                },
            },
            searching: false,
            destroy: true,
            language: {
                url: this.languageUrl,
            },
            ajax: (dataTablesParams: any, callback: any) => {
                const searchData: VestraUserSearchData  = {
                    nameStart: this.searchValue,
                };

                for (let order of dataTablesParams.order) {
                    order.name = dataTablesParams.columns[order.column]?.data;
                }

                const model: DataTableRequest = {
                    draw: dataTablesParams.draw,
                    start: dataTablesParams.start,
                    length: dataTablesParams.length,
                    order: dataTablesParams.order,
                    search: searchData,
                    columns: this.transformToColumnData(this.dtOptions.columns),
                };

                this.datatableRequest = model;

                this.handleSpinner(firstValueFrom(this.vestraUserService.getVestraUserSelectListDataSource(model)))
                    .then(res => {
                        callback(this.getTableData(res));
                        return Promise.resolve(res);
                    })
                    .catch((err: HttpErrorResponse) => {
                        this.validationHandlerService.handleServerError(err);
                        callback(this.getEmptyTableData());
                    });
            },
            columns: columns
        };

        return res;
    }

    public onSearch(event: string): void {
        this.searchValue = event;
        this.refreshDataTable();
    }

    public onProviderSelect(event: { action: string; id: number }) {
        if (event.action === 'push') {
            this.userIds.push(event.id);
        } else if (event.action === 'remove') {
            this.userIds = this.userIds.filter(id => id !== event.id);
        }

        this.selectedUserIds.emit(this.userIds);
    }
    private getColumns(): ADTColumns[] {
        let translateService = this.translateService;
        let columns: ADTColumns[] = [
            {
                data: 'id',
                title: 'common.id',
                searchable: false,
                orderable: true,
            },
            {
                data: 'fullName',
                title: 'common.name',
                responsivePriority: 2,
                searchable: false,
                orderable: true,
            }
        ];
        if(this.vestraUserSelectType == VestraUserSelectType.InAppAndPush)
        {
            columns.push(
            {
                data: 'isConnectedToFCM',
                title: 'common.push_message_receive',
                searchable: false,
                orderable: true,
                render: function (data) {
                    if (data == true) {
                        return translateService.instant('common.yes');
                    }
                    return translateService.instant('common.no');
                },
            })
        }
        else
        {
            columns.push(
            {
                data: 'isNewsLetterEnabled',
                title: 'common.newsletter_receive',
                searchable: false,
                orderable: true,
                render: function (data) {
                    if (data == true) {
                        return translateService.instant('common.yes');
                    }
                    return translateService.instant('common.no');
                },
            })
        }

        columns.push(
        {
            data: 'isEnabled',
            title: 'common.vestra_user_select_selected',
            searchable: false,
            orderable: false,
            responsivePriority: 1,
            defaultContent: '',
            ngTemplateRef: {
                ref: this.vestraUserSelectCheckboxRef,
                context: {
                    captureEvents: this.onProviderSelect.bind(this),
                },
            },
        })

        return columns;
    }
}
